// One collapsible section ("wall", "ceiling", ...) of the mapping table.
import { AlertTriangle, ChevronDown, ChevronRight } from "lucide-react";
import { Pill, TD, TD_NUM } from "../../../components/ui";
import { ConfidenceBadge } from "../../../components/EstimateBadges";
import { fmtQty } from "../../../utils/format";
import SurfaceCell from "./SurfaceCell";
import { getStatus, sfQtyFor } from "./mapping-utils";

const STATUS_TONE = { Accepted: "success", Suggested: "info", Unmapped: "danger", Skipped: "neutral" };

const CHECKBOX_CLS = "h-3.5 w-3.5 cursor-pointer accent-[var(--color-primary)]";

export default function MappingGroup({
  group,
  allInGroup,
  mappings,
  surfaceById,
  surfaceOptions,
  skipUnmapped,
  collapsed,
  onToggleCollapse,
  selected,
  onToggleRow,
  onToggleGroup,
  editingId,
  setEditingId,
  onPick,
}) {
  const mappedInGroup = allInGroup.filter((l) => mappings[l.id]?.surfaceId).length;
  const visibleSelected = group.lines.filter((l) => selected.has(l.id)).length;
  const allSelected = group.lines.length > 0 && visibleSelected === group.lines.length;

  return (
    <tbody>
      <tr className="bg-surface_alt">
        <td className="w-10 border-b border-border px-3 py-1.5">
          <input
            type="checkbox"
            className={CHECKBOX_CLS}
            aria-label={`Select all ${group.label} lines`}
            checked={allSelected}
            ref={(el) => {
              if (el) el.indeterminate = visibleSelected > 0 && !allSelected;
            }}
            onChange={() => onToggleGroup(group.lines, !allSelected)}
          />
        </td>
        <td colSpan={8} className="border-b border-border px-2 py-1.5">
          <button
            type="button"
            onClick={onToggleCollapse}
            aria-expanded={!collapsed}
            className="inline-flex items-center gap-2 rounded-[4px] px-1 py-0.5 text-[12.5px] font-semibold text-text_primary focus:outline-none focus-visible:ring-2 focus-visible:ring-border_strong"
          >
            {collapsed ? <ChevronRight size={14} aria-hidden="true" /> : <ChevronDown size={14} aria-hidden="true" />}
            <span className="font-mono text-[11.5px] lowercase text-text_muted">{group.section}</span>
            {group.label}
            <Pill tone={mappedInGroup === allInGroup.length ? "success" : "neutral"}>
              {mappedInGroup}/{allInGroup.length} mapped
            </Pill>
          </button>
        </td>
      </tr>

      {!collapsed &&
        group.lines.map((line) => {
          const m = mappings[line.id];
          const surface = m?.surfaceId ? surfaceById[m.surfaceId] : null;
          const status = getStatus(m, skipUnmapped);
          const sf = sfQtyFor(line, surface);
          const isSelected = selected.has(line.id);
          return (
            <tr key={line.id} className={isSelected ? "bg-primary_tint" : "bg-surface"}>
              <td className={`${TD} w-10`}>
                <input
                  type="checkbox"
                  className={CHECKBOX_CLS}
                  aria-label={`Select ${line.rawName}`}
                  checked={isSelected}
                  onChange={() => onToggleRow(line.id)}
                />
              </td>
              <td className={`${TD} max-w-[340px]`}>
                <span className="block truncate" title={line.rawName}>
                  {line.rawName}
                </span>
              </td>
              <td className={`${TD} max-w-[220px] text-text_secondary`}>
                <span className="block truncate" title={line.location}>
                  {line.location}
                </span>
              </td>
              <td className={TD_NUM}>{line.qty > 0 ? fmtQty(line.qty) : <span className="text-warning">—</span>}</td>
              <td className={`${TD} ${line.unit ? "text-text_secondary" : "font-semibold text-warning"}`}>{line.rawUnit}</td>
              <td className={`${TD_NUM} text-text_secondary`}>{sf != null ? fmtQty(sf) : "—"}</td>
              <td className={`${TD} py-1`}>
                <SurfaceCell
                  line={line}
                  surfaceId={m?.surfaceId}
                  surfaceName={surface?.name}
                  options={surfaceOptions}
                  editing={editingId === line.id}
                  onEdit={() => setEditingId(line.id)}
                  onPick={(surfaceId) => onPick(line.id, surfaceId)}
                  onClose={() => setEditingId((cur) => (cur === line.id ? null : cur))}
                />
              </td>
              <td className={TD}>
                <ConfidenceBadge confidence={line.confidence} />
              </td>
              <td className={TD}>
                <span className="inline-flex items-center gap-1.5">
                  <Pill tone={STATUS_TONE[status]}>{status}</Pill>
                  {line.warnings.length > 0 && (
                    <span className="inline-flex text-warning" title={line.warnings.join(" · ")}>
                      <AlertTriangle size={14} aria-hidden="true" />
                      <span className="sr-only">Warning: {line.warnings.join(", ")}</span>
                    </span>
                  )}
                </span>
              </td>
            </tr>
          );
        })}
    </tbody>
  );
}
